import { useState, useMemo, useEffect, useRef } from 'react';
import {
  DialogContent,
  Box,
  styled,
  Typography,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import get from 'lodash/get';
import { number, object, string } from 'yup';

import { getUsers } from '../../api/users/users.api';
import { useAccountDetails } from '../../components/hooks/queries/accounts/useAccountDetails';
import { SelectV2 } from '../../components/inputs/selects/SelectV2';
import CbButton from '../../components/Buttons/CbButton';
import { activateWizer } from '../../api/resilience/resilience.api';
import { AxiosError } from 'axios';
import useNotifications from '../../_lib/notifications/useNotifications';

interface WizerActivationModalProps {
  data: {
    accountId: string;
    onClose: () => void;
  };
}

const MIN_LICENSES = 1;
const MAX_LICENSES = 10000;

const wizerActivationSchema = object().shape({
  accountId: string().required('Account ID is required'),
  cbid: string().required('CBID is required'),
  firstName: string().required('First name is required'),
  lastName: string().required('Last name is required'),
  email: string().required('Email is required'),
  companyName: string().required('Company name is required'),
  licensesToAllocate: number()
    .integer('Licenses must be a whole number')
    .default(1)
    .typeError('License count is required')
    .required('License count is required')
    .min(MIN_LICENSES, `At least ${MIN_LICENSES} license is required`)
    .max(MAX_LICENSES, `Maximum ${MAX_LICENSES} licenses allowed`),
});

const WizerActivationModal = ({ data }: WizerActivationModalProps) => {
  const notifications = useNotifications();
  const [selectedPolicyholder, setSelectedPolicyholder] = useState('');
  const [confirmationStep, setConfirmationStep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [licensesToAllocate, setLicensesToAllocate] = useState<number | ''>(1);
  const queryClient = useQueryClient();

  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['/api/auth/v1/users', { accountId: data.accountId, size: 100 }],
    queryFn: () =>
      getUsers({ params: { size: 100 } }).then((res) =>
        get(res, 'data.data', [])
      ),
    enabled: !!data.accountId,
  });

  const { data: account, isLoading: isLoadingAccount } = useAccountDetails(
    data.accountId
  );

  const isInitialized = useRef(false);

  useEffect(() => {
    if (account?.noOfEmployeesAll && !isInitialized.current) {
      setLicensesToAllocate(+account.noOfEmployeesAll);
      isInitialized.current = true;
    }
  }, [account?.noOfEmployeesAll]);

  const userOptions = useMemo(
    () =>
      users.map((_user) => ({
        label: `${_user?.firstName} ${_user?.lastName} - ${_user?.role?.name} (${_user?.email})`,
        value: _user?.id,
      })),
    [users]
  );

  const selectedPolicyholderDetails = users.find(
    (user) => user.id === selectedPolicyholder
  );

  const handleReview = () => {
    if (!selectedPolicyholder) {
      notifications.warning('Please select a policyholder');
      return;
    }
    setConfirmationStep(true);
  };

  const handleBack = () => {
    setConfirmationStep(false);
  };

  const onSubmit = async () => {
    try {
      if (!account) {
        notifications.error(
          'Failed to retrieve account details. Please try again.'
        );
        return;
      }

      setIsSubmitting(true);

      const payload = {
        accountId: account.id,
        cbid: account.cbid,
        firstName: selectedPolicyholderDetails?.firstName,
        lastName: selectedPolicyholderDetails?.lastName,
        email: selectedPolicyholderDetails?.email,
        companyName: account.name,
        licensesToAllocate,
      };

      await wizerActivationSchema.validate(payload);
      await activateWizer({ data: payload });
      await queryClient.invalidateQueries(['wizer-training']);

      notifications.success('Wizer activated successfully');
      data.onClose();
    } catch (error) {
      console.error(error);
      if (error.name === 'ValidationError') {
        notifications.error('Validation error: ' + error.message);
        return;
      }

      if (error instanceof AxiosError) {
        notifications.error(
          'Failed to activate Wizer: ' +
            (error.response?.data?.message || 'Server error')
        );
        return;
      }

      notifications.error('Failed to activate Wizer: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingUsers || isLoadingAccount) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{ mx: 2, my: 1 }}
      role="dialog"
      aria-labelledby="wizer-activation-title"
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="500">
          Important: Wizer activation is a one-time process that cannot be
          reversed. Please verify all details carefully before proceeding.
        </Typography>
      </Alert>

      <DialogContent sx={{ p: `0 !important` }}>
        {!confirmationStep ? (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are about to activate Wizer cybersecurity awareness training
              for a policyholder. This will:
            </Typography>

            <Box component="ul" sx={{ pl: 2, mb: 3 }}>
              <li>
                Grant the selected user access to Wizer training platform admin
                access
              </li>
              <li>Allocate licenses based on the company's employee count</li>
              <li>Enable the Wizer connector on behalf of the policyholder</li>
            </Box>

            <Box display="grid" sx={{ mb: 3 }}>
              <Label htmlFor="selectedPolicyholder">Select policyholder*</Label>
              <SelectV2
                id="selectedPolicyholder"
                placeholder="Select policyholder"
                options={userOptions}
                name="selectedPolicyholder"
                required
                value={selectedPolicyholder}
                onChange={(e) => {
                  setSelectedPolicyholder(e.target.value as string);
                }}
                sx={{ mt: 1 }}
                aria-required="true"
              />
            </Box>
            <Box display="grid" sx={{ mb: 3 }}>
              <Label htmlFor="licensesToAllocate">Licenses to allocate*</Label>
              <TextField
                id="licensesToAllocate"
                type="number"
                size="small"
                value={licensesToAllocate}
                onChange={(e) => {
                  const { value } = e.target;

                  if (value === '') {
                    setLicensesToAllocate('');
                    return;
                  }

                  const num = parseInt(value, 10);

                  if (
                    !isNaN(num) &&
                    Number.isInteger(num) &&
                    num >= MIN_LICENSES &&
                    num <= MAX_LICENSES
                  ) {
                    setLicensesToAllocate(num);
                  }
                }}
                inputProps={{
                  min: MIN_LICENSES,
                  max: MAX_LICENSES,
                }}
                sx={{ mt: 1 }}
                aria-required="true"
              />
            </Box>
            <Box display="flex" gap={1} justifyContent="flex-end" mb={1}>
              <CbButton styleName="secondary" onClick={data.onClose}>
                Cancel
              </CbButton>
              <CbButton
                styleName="primary"
                disabled={!selectedPolicyholder || !licensesToAllocate}
                onClick={handleReview}
              >
                Review Details
              </CbButton>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600 }}
              id="wizer-activation-title"
            >
              Confirm Wizer Activation Details
            </Typography>

            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                p: 2,
                borderRadius: 1,
                mb: 3,
              }}
            >
              <DetailRow
                label="Policyholder"
                value={`${selectedPolicyholderDetails?.firstName} ${selectedPolicyholderDetails?.lastName}`}
              />
              <DetailRow
                label="Email"
                value={selectedPolicyholderDetails?.email}
              />
              <DetailRow label="Company" value={account?.name} />
              <DetailRow
                label="Licenses to Allocate"
                value={licensesToAllocate}
              />
              <DetailRow label="CBID" value={account?.cbid} />
            </Box>

            <Alert
              severity="warning"
              sx={{
                mb: 3,
                background: (theme) => theme.palette.common.lightYellow,
                color: (theme) => theme.palette.common.yellow,
              }}
            >
              <Typography variant="body2">
                This action cannot be undone. Once activated, Wizer training
                will be enabled for the company on behalf of selected
                policyholder.
              </Typography>
            </Alert>

            <Box display="flex" gap={1} justifyContent="flex-end" mb={1}>
              <CbButton
                styleName="secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </CbButton>
              <CbButton
                styleName="primary"
                onClick={onSubmit}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Activating...' : 'Confirm Activation'}
              </CbButton>
            </Box>
          </>
        )}
      </DialogContent>
    </Box>
  );
};

const Label = styled('label')(({ theme }) => ({
  fontSize: theme.config.textSizes.normal,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined;
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="body2" fontWeight="600">
      {label}:
    </Typography>
    <Typography variant="body2">{value || 'N/A'}</Typography>
  </Box>
);

export const WizerActivationModalConfig = {
  WizerActivationModal: {
    component: WizerActivationModal,
    config: {
      fullWidth: true,
      title: 'Activate Wizer Security Training',
      maxWidth: 'sm',
    },
  },
};
